// Shared layout for the post-registration role preview pages.
// Shows: hero header, what they get post-approval, animated mock dashboard,
// 3 next steps, and a "Request Demo Access" CTA that signals high intent
// to admins WITHOUT granting demo access automatically. Once admin approves
// the application, this page auto-flips to show an "Open Live Demo" CTA
// that grants a read-only, PII-redacted view of the platform.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Sparkles, Lock, Send, Eye } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, delay } },
});

export default function RolePreviewLayout({
  role, accent, eyebrow, headline, subline,
  benefits, dashboard, nextSteps, primaryStat,
}) {
  const [params] = useSearchParams();
  const appId = params.get("app_id") || "";
  const [requested, setRequested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [statusInfo, setStatusInfo] = useState(null);

  // Hydrate state if user has already requested earlier (refresh-safe)
  useEffect(() => {
    if (!appId) return;
    let alive = true;
    axios.get(`${API}/public/application-interest/${appId}`)
      .then((r) => { if (alive && r.data?.demo_interest) setRequested(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, [appId]);

  // Poll application status — when admin approves, surface the demo URL.
  useEffect(() => {
    if (!appId) return;
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/public/application-status/${appId}`);
        if (alive) setStatusInfo(data);
      } catch (_) { /* silent */ }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [appId]);

  const isApproved = statusInfo?.status === "approved";
  const demoUrl = statusInfo?.demo_url || statusInfo?.investor_demo_url;

  const requestDemoAccess = async () => {
    if (!appId) {
      setConfirmMsg("We need your application reference to record interest. Please apply first from the homepage.");
      return;
    }
    if (requested || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications/express-interest`, { application_id: appId });
      setRequested(true);
      setConfirmMsg(data?.message || "Thanks — your interest is noted. Our team will be in touch within 24 hours.");
    } catch (e) {
      setConfirmMsg("Could not record interest right now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A07] text-white" data-testid={`welcome-${role}`}>
      {/* Header */}
      <header className="border-b border-[#1A2E22]">
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" aria-label="Delta GreenX home">
            <BrandLogo as="div" size="md" />
          </Link>
          <Link to="/" className="text-[#A1B2A8] hover:text-white text-xs flex items-center gap-1">
            ← Back to landing
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pt-10 sm:pt-12 pb-8 sm:pb-10">
        <motion.div {...fadeUp(0)} className="flex items-center gap-2 mb-4">
          <CheckCircle className="w-4 h-4 text-[#22C55E]" />
          <span className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-semibold">{eyebrow}</span>
        </motion.div>
        <motion.h1 {...fadeUp(0.05)} className="font-['Outfit'] text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] sm:leading-[1.05] tracking-tight">
          {headline}
        </motion.h1>
        <motion.p {...fadeUp(0.12)} className="text-[#C5D5CC] text-sm sm:text-lg mt-4 sm:mt-5 max-w-2xl leading-relaxed">
          {subline}
        </motion.p>
        {primaryStat && (
          <motion.div {...fadeUp(0.18)} className={`mt-6 inline-flex items-baseline gap-3 border ${accent.border} ${accent.bgSoft} px-4 sm:px-5 py-3`}>
            <span className={`font-['Outfit'] text-2xl sm:text-3xl font-bold ${accent.text}`}>{primaryStat.value}</span>
            <span className="text-[#A1B2A8] text-[10px] sm:text-xs uppercase tracking-wider">{primaryStat.label}</span>
          </motion.div>
        )}
      </section>

      {/* Mock Dashboard */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pb-8 sm:pb-10">
        <motion.div {...fadeUp(0.22)} className="border border-[#1A2E22] bg-[#09120D] overflow-hidden">
          <div className="border-b border-[#1A2E22] px-4 sm:px-5 py-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className={`w-3.5 h-3.5 ${accent.text} flex-shrink-0`} />
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-semibold text-[#A1B2A8] truncate">
                Preview · what you&apos;ll see post-approval
              </span>
            </div>
            <span className="hidden sm:inline text-[10px] text-[#A1B2A8]/50 flex-shrink-0">{role} dashboard</span>
          </div>
          <div className="p-4 sm:p-6">{dashboard}</div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pb-8 sm:pb-10">
        <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-4">What you unlock</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {benefits.map((b, i) => (
            <motion.div key={i} {...fadeUp(0.25 + i * 0.05)} className="border border-[#1A2E22] bg-[#09120D] p-4 sm:p-5">
              <b.icon className={`w-5 h-5 ${accent.text} mb-3`} />
              <p className="text-white font-['Outfit'] font-semibold text-sm mb-1">{b.title}</p>
              <p className="text-[#A1B2A8] text-xs leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Next Steps */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pb-8 sm:pb-10">
        <h2 className="text-[#A1B2A8] text-xs font-semibold uppercase tracking-[0.2em] mb-4">What happens next</h2>
        <div className="space-y-3">
          {nextSteps.map((s, i) => (
            <motion.div key={i} {...fadeUp(0.3 + i * 0.05)} className="flex items-start gap-3 sm:gap-4 border border-[#1A2E22] bg-[#09120D] p-4 sm:p-5">
              <span className={`font-['Outfit'] text-lg sm:text-xl font-bold ${accent.text} w-7 sm:w-8 flex-shrink-0`}>{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold">{s.title}</p>
                <p className="text-[#A1B2A8] text-xs mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Demo Access — APPROVED state */}
      {isApproved && demoUrl && (
        <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pb-12 sm:pb-16">
          <motion.div {...fadeUp(0.5)} className={`border ${accent.border} bg-[#22C55E]/5 p-5 sm:p-8`} data-testid={`welcome-${role}-approved`}>
            <div className="flex items-start gap-3 mb-5">
              <Sparkles className={`w-6 h-6 ${accent.text} mt-1 flex-shrink-0`} />
              <div>
                <p className={`font-['Outfit'] text-2xl font-bold ${accent.text}`}>Your live demo is ready.</p>
                <p className="text-[#A1B2A8] text-sm mt-1 max-w-xl">
                  Read-only · 30-min session. See exactly how Delta GreenX operates — capital lifecycle, AI confidence scoring, supply-chain mapping, and live pilot economics.
                </p>
                <p className="text-[#A1B2A8]/50 text-[10px] mt-2 italic">
                  This is a demo session. Operating controls remain with the Delta GreenX team — full participant access is granted post formal onboarding.
                </p>
              </div>
            </div>
            <a
              href={demoUrl}
              className={`w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-2 px-6 py-3.5 ${accent.bg} text-[#050A07] font-['Outfit'] font-semibold text-sm hover:opacity-90 transition-opacity`}
              data-testid={`welcome-${role}-open-demo-btn`}
            >
              <Eye className="w-4 h-4" /> Open Live Demo <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-[#A1B2A8]/40 text-[10px] mt-3 break-all font-mono">{demoUrl}</p>
          </motion.div>
        </section>
      )}

      {/* Demo Access — PENDING (request-only) */}
      {!isApproved && (
        <section className="max-w-[1200px] mx-auto px-5 sm:px-6 pb-12 sm:pb-16">
          <motion.div {...fadeUp(0.5)} className={`border ${accent.border} ${accent.bgSoft} p-5 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4`} data-testid={`welcome-${role}-demo-cta`}>
            <div className="flex items-start gap-3">
              <Lock className={`w-5 h-5 mt-1 flex-shrink-0 ${accent.text}`} />
              <div>
                <p className={`font-['Outfit'] text-lg sm:text-xl font-bold ${accent.text}`}>
                  {requested ? "Demo access requested" : "Want to explore the live infrastructure?"}
                </p>
                <p className="text-[#A1B2A8] text-sm mt-1 leading-relaxed max-w-xl">
                  {requested
                    ? confirmMsg || "Thanks — your interest is noted. Our team will be in touch within 24 hours."
                    : "Live demo access is reserved for verified, approved participants. Click below — our team will reach out within 24 hours after a quick eligibility check. Demo is read-only; full operating access is granted post formal onboarding."}
                </p>
              </div>
            </div>
            {!requested && (
              <button
                onClick={requestDemoAccess}
                disabled={submitting}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 ${accent.bg} text-[#050A07] font-['Outfit'] font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity flex-shrink-0`}
                data-testid={`welcome-${role}-request-demo-btn`}
              >
                {submitting ? "Recording…" : <><Send className="w-4 h-4" /> Request Demo Access <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
            {requested && (
              <span
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E] font-['Outfit'] font-semibold text-sm flex-shrink-0"
                data-testid={`welcome-${role}-request-confirmed`}
              >
                <CheckCircle className="w-4 h-4" /> Interest received
              </span>
            )}
          </motion.div>
          {confirmMsg && !requested && (
            <p className="text-yellow-400/80 text-xs mt-3" data-testid={`welcome-${role}-request-error`}>{confirmMsg}</p>
          )}
        </section>
      )}

      <footer className="border-t border-[#1A2E22] py-6 text-center text-[#A1B2A8]/40 text-xs">
        Delta GreenX · Operating Infrastructure for Global Food Commerce
      </footer>
    </div>
  );
}
